import {
  DateFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { CompanyBlackListRepository } from '@/store/company-blacklist-state/company-blacklist-state.repository';
import { CompanyList, DecisionEnum } from '@/types';
import { map, of } from 'rxjs';

interface ICompanySubmissionsBlackListAddFormObject {
  store: CompanyBlackListRepository;
  companyOptions: CompanyList[];
}

export const getCompanySubmissionsBlackListAddFormObject = ({ store, companyOptions }: ICompanySubmissionsBlackListAddFormObject): DynamicForm => {
  const companySelectField: DynamicField<SelectFieldProps> = {
    label: 'settings.blackList.form.company.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getObservable<SelectOption>('selectedCompany'),
      placeholder: 'settings.blackList.form.company.placeholder',
      options: companyOptions.map(company => ({ name: company.nom, code: company.id })),
      onSelectChange: (value: SelectOption) => {
        store.setNested('selectedCompany', value);
        store.setNested('dto', 'societeId', value.code);
        store.setNested('selectedCompanyObject', companyOptions.find(company => company.id === value.code) || null);
      },
    },
  };

  const companyNameField: DynamicField<TextFieldProps> = {
    label: 'settings.blackList.form.companyName.label',
    variant: FieldVariant.TEXT,
    isDisabled: of(true),
    isHidden: store.getObservable('selectedCompanyObject').pipe(map(obj => obj === null)),
    props: {
      value: store.getNestedObservable<string>('selectedCompanyObject', 'nom'),
      placeholder: 'settings.blackList.form.companyName.placeholder',
    },
  };

  const companyNifField: DynamicField<TextFieldProps> = {
    label: 'settings.blackList.form.companyNif.label',
    variant: FieldVariant.TEXT,
    isDisabled: of(true),
    isHidden: store.getObservable('selectedCompanyObject').pipe(map(obj => obj === null)),
    props: {
      value: store.getNestedObservable<string>('selectedCompanyObject', 'nif'),
      placeholder: 'settings.blackList.form.companyNif.placeholder',
    },
  };

  const companyCountryField: DynamicField<TextFieldProps> = {
    label: 'settings.blackList.form.companyCountry.label',
    variant: FieldVariant.TEXT,
    isDisabled: of(true),
    isHidden: store.getObservable('selectedCompanyObject').pipe(map(obj => obj === null)),
    props: {
      value: store.getNestedObservable<string>('selectedCompanyObject', 'pays'),
    },
  };

  const decisionSelectField: DynamicField<SelectFieldProps> = {
    label: 'settings.blackList.form.decision.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      value: store.getNestedObservable<SelectOption>('dto', 'natureDecision'),
      placeholder: 'settings.blackList.form.decision.placeholder',
      options: [
        {
          name: 'settings.blackList.form.decision.temporary',
          code: DecisionEnum.TEMPORAIRE,
        },
        {
          name: 'settings.blackList.form.decision.permanent',
          code: DecisionEnum.PERMANENT,
        },
      ],
      onSelectChange: (value: SelectOption) => {
        store.setNested('dto', 'natureDecision', value);
      },
    },
  };

  const effectiveDateField: DynamicField<DateFieldProps> = {
    label: 'settings.blackList.form.effectiveDate.label',
    variant: FieldVariant.DATE,
    error: store.getNestedObservable<string>('errors', 'dateEffet'),
    isRequired: true,
    props: {
      view: 'date',
      value: store.getNestedObservable<Date>('dto', 'dateEffet'),
      onChange: (value: Date) => {
        store.setNested('dto', 'dateEffet', value);
        store.clearError('dateEffet');
      },
    },
  };

  const deadlineField: DynamicField<DateFieldProps> = {
    label: 'settings.blackList.form.deadline.label',
    variant: FieldVariant.DATE,
    error: store.getNestedObservable<string>('errors', 'delai'),
    isRequired: true,
    isHidden: store
      .getNestedObservable<SelectOption>('dto', 'natureDecision')
      .pipe(map(decision => (decision?.code === DecisionEnum.TEMPORAIRE ? false : true))),
    props: {
      view: 'date',
      value: store.getNestedObservable<Date>('dto', 'delai'),
      onChange: (value: Date) => {
        store.setNested('dto', 'delai', value);
        store.clearError('delai');
      },
    },
  };

  return {
    title: 'settings.blackList.form.title',
    isHeaderHidden: true,
    orientation: 'vertical',
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [companySelectField],
          },
          {
            fields: [companyNameField, companyNifField, companyCountryField],
          },
          {
            fields: [decisionSelectField, effectiveDateField, deadlineField],
          },
        ],
      },
    ],
  };
};
